import type { AppStore } from '@/types/store';
import type { AllergenDetection, PALDetection } from '@allergen-lens/shared-logic';
import { Confidence } from '@allergen-lens/shared-logic';
import type { SeverityLevel } from '@/types/profile';
import substitutionData from '@/data/substitutions.json';
import ambiguousMapData from '@/data/ambiguousAllergenMap.json';

interface Alternative {
  name: string;
  notes: string;
  containsAllergens?: string[];
}

interface Substitution {
  allergenCategory: string;
  allergenId: string;
  alternatives: Alternative[];
}

const substitutions = substitutionData.substitutions as Substitution[];

interface AmbiguousTermEntry {
  term: string;
  potentialAllergens: string[];
}

const ambiguousMap = ambiguousMapData.ambiguousTerms as AmbiguousTermEntry[];

export const selectDetections = (state: AppStore) => state.results;
export const selectUniqueAllergens = (state: AppStore) =>
  [...new Set(state.results.map((d) => d.allergenCategory).filter((c) => c !== 'unknown'))];
export const selectPresentDetections = (state: AppStore) =>
  state.results.filter((d) => d.confidence === Confidence.Present || d.confidence === Confidence.Possible);
export const selectReviewDetections = (state: AppStore) =>
  state.results.filter((d) => d.confidence === Confidence.NeedsReview);
export const selectDeclaration = (state: AppStore) => state.declaration;
export const selectActiveProfile = (state: AppStore) =>
  state.profiles.find((p) => p.id === state.activeProfileId) ?? null;
export const selectAllergenCount = (state: AppStore) =>
  [...new Set(state.results.map((d) => d.allergenCategory).filter((c) => c !== 'unknown'))].length;
export const selectStatusText = (state: AppStore) =>
  `${state.tokens.length} ingredients \u2022 ${selectAllergenCount(state)} allergens \u2022 ${state.mode} mode`;

// ── F-39 (E1): Profile-Aware Safety Verdict ──────

export const selectProfileHits = (state: AppStore): AllergenDetection[] => {
  const profile = state.profiles.find((p) => p.id === state.activeProfileId);
  if (!profile) return [];
  const allergenSet = new Set<string>([
    ...profile.selectedAllergens,
    ...profile.customAllergens.map((ca) => ca.id),
  ]);
  return state.results.filter(
    (d) => (d.confidence === Confidence.Present || d.confidence === Confidence.Possible)
      && allergenSet.has(d.allergenId)
  );
};

// ── F-42 (E4): Certification Contradiction Check ──────

const CERT_CONTRADICTIONS: Record<string, string[]> = {
  'Gluten-Free Certified': ['cereals_containing_gluten'],
  'Nut-Free Certified': ['tree_nuts', 'peanuts'],
  'Dairy-Free Certified': ['milk'],
  'Egg-Free Certified': ['eggs'],
  'Soy-Free Certified': ['soybeans'],
  'Peanut-Free': ['peanuts'],
  'Sesame-Free': ['sesame'],
  'Top-8 Allergen Free': ['milk', 'eggs', 'fish', 'crustaceans', 'tree_nuts', 'peanuts', 'cereals_containing_gluten', 'soybeans'],
  'Top-14 Allergen Free': ['milk', 'eggs', 'fish', 'crustaceans', 'tree_nuts', 'peanuts', 'cereals_containing_gluten', 'soybeans', 'celery', 'mustard', 'sesame', 'sulphur_dioxide_sulphites', 'lupin', 'molluscs'],
  'Vegan Certified': ['milk', 'eggs', 'fish', 'crustaceans'],
};

export interface CertificationContradiction {
  certification: string;
  contradictedAllergens: string[];
}

export const selectCertificationContradictions = (state: AppStore): CertificationContradiction[] => {
  const presentCategories = new Set(
    state.results
      .filter((d) => d.confidence === Confidence.Present || d.confidence === Confidence.Possible)
      .map((d) => d.allergenCategory)
  );
  return state.certificationMatches
    .map((cert) => {
      const excluded = CERT_CONTRADICTIONS[cert.name] ?? [];
      const contradicted = excluded.filter((cat) => presentCategories.has(cat));
      return { certification: cert.name, contradictedAllergens: contradicted };
    })
    .filter((c) => c.contradictedAllergens.length > 0);
};

// ── F-43 (E7): Substitution Safety Screening ──────

export interface SubstitutionSafetyResult {
  [substituteName: string]: { safe: boolean; conflictingAllergenIds: string[] };
}

export const selectSubstitutionSafety = (state: AppStore): SubstitutionSafetyResult | null => {
  const profile = state.profiles.find((p) => p.id === state.activeProfileId);
  if (!profile) return null;

  const userAllergens = new Set<string>([
    ...profile.selectedAllergens,
    ...profile.customAllergens.map((ca) => ca.id),
  ]);

  const result: SubstitutionSafetyResult = {};
  for (const sub of substitutions) {
    for (const alt of sub.alternatives) {
      const tags = alt.containsAllergens ?? [];
      const conflicting = tags.filter((id) => userAllergens.has(id));
      result[alt.name] = {
        safe: conflicting.length === 0,
        conflictingAllergenIds: conflicting,
      };
    }
  }
  return result;
};

// ── F-44 (E10): Profile-Aware Review Prioritization ──────

export type ReviewPriority = 'high' | 'medium' | 'low';

export interface ReviewPriorityItem {
  detection: AllergenDetection;
  priority: ReviewPriority;
  hiddenAllergenIds: string[];
}

export const selectReviewPriorities = (state: AppStore): ReviewPriorityItem[] => {
  const profile = state.profiles.find((p) => p.id === state.activeProfileId);
  const reviewItems = state.results.filter((d) => d.confidence === Confidence.NeedsReview);

  if (!profile) {
    return reviewItems.map((detection) => ({ detection, priority: 'low' as const, hiddenAllergenIds: [] }));
  }

  const allergenSet = new Set<string>([
    ...profile.selectedAllergens,
    ...profile.customAllergens.map((ca) => ca.id),
  ]);

  return reviewItems.map((detection) => {
    const entry = ambiguousMap.find((a) => a.term === detection.matchedTerm);
    if (!entry) {
      return { detection, priority: 'low' as const, hiddenAllergenIds: [] };
    }
    const hidden = entry.potentialAllergens.filter((id) => allergenSet.has(id));
    return {
      detection,
      priority: hidden.length > 0 ? 'high' as const : 'medium' as const,
      hiddenAllergenIds: hidden,
    };
  });
};

// ── F-46 (E12): Cross-Contact Risk Confirmation ──────

export const selectCrossContactConfirmations = (state: AppStore) => {
  const detectedIds = new Set(state.results.map((d) => d.allergenId));
  return state.crossContactRisks.map((risk) => ({
    ...risk,
    confirmedInIngredients: risk.allergens.filter((a) => detectedIds.has(a)),
    crossContactOnly: risk.allergens.filter((a) => !detectedIds.has(a)),
  }));
};

// ── F-47 (E1): Multi-Profile Safety Verdict ──────

export interface ProfileVerdict {
  profileId: string;
  profileName: string;
  hits: AllergenDetection[];
}

export const selectAllProfileVerdicts = (state: AppStore): ProfileVerdict[] => {
  return state.profiles
    .filter((p) => p.id !== state.activeProfileId)
    .map((profile) => {
      const allergenSet = new Set<string>([
        ...profile.selectedAllergens,
        ...profile.customAllergens.map((ca) => ca.id),
      ]);
      const hits = state.results.filter(
        (d) =>
          (d.confidence === Confidence.Present || d.confidence === Confidence.Possible) &&
          allergenSet.has(d.allergenId)
      );
      return { profileId: profile.id, profileName: profile.name, hits };
    });
};

// ── F-49 (E3): Profile-Aware PAL Prioritization ──────

export type PALPriority = 'critical' | 'watch' | 'informational';

export interface ClassifiedPAL {
  pal: PALDetection;
  priority: PALPriority;
  matchedAllergenIds: string[];
}

const PAL_NAME_TO_IDS: Record<string, string[]> = {
  milk: ['milk'], eggs: ['eggs'], peanut: ['peanuts'], peanuts: ['peanuts'],
  'tree nuts': ['tree_nut_almond', 'tree_nut_hazelnut', 'tree_nut_walnut', 'tree_nut_cashew',
    'tree_nut_pecan', 'tree_nut_brazil', 'tree_nut_pistachio', 'tree_nut_macadamia'],
  nuts: ['tree_nut_almond', 'tree_nut_hazelnut', 'tree_nut_walnut', 'tree_nut_cashew',
    'tree_nut_pecan', 'tree_nut_brazil', 'tree_nut_pistachio', 'tree_nut_macadamia', 'peanuts'],
  soy: ['soybeans'], soya: ['soybeans'],
  wheat: ['wheat'], gluten: ['wheat', 'rye', 'barley', 'oats'],
  fish: ['fish_general', 'fish_salmon', 'fish_tuna', 'fish_cod', 'fish_anchovy'],
  shellfish: ['crustacean_general', 'crustacean_shrimp', 'crustacean_crab', 'crustacean_lobster',
    'molluscs'],
  sesame: ['sesame'], celery: ['celery'], mustard: ['mustard'],
  sulphites: ['sulphur_dioxide_sulphites'], sulfites: ['sulphur_dioxide_sulphites'], lupin: ['lupin'],
};

export function resolvePALAllergenIDs(referenced: string[]): string[] {
  const ids = new Set<string>();
  for (const name of referenced) {
    const lower = name.toLowerCase().replace(/\.$/, '');
    const mapped = PAL_NAME_TO_IDS[lower];
    if (mapped) mapped.forEach((id) => ids.add(id));
  }
  return [...ids];
}

export const selectPrioritizedPALs = (state: AppStore): ClassifiedPAL[] => {
  const profile = state.profiles.find((p) => p.id === state.activeProfileId);

  if (!profile) {
    return state.palDetections.map((pal) => ({
      pal,
      priority: 'informational' as const,
      matchedAllergenIds: [],
    }));
  }

  const allergenSet = new Set<string>([
    ...profile.selectedAllergens,
    ...profile.customAllergens.map((ca) => ca.id),
  ]);
  const severityMap = new Map<string, SeverityLevel>();
  for (const s of profile.allergenSeverities) severityMap.set(s.allergenId, s.severity);

  return state.palDetections.map((pal) => {
    const ids = resolvePALAllergenIDs(pal.referencedAllergens);
    const matched = ids.filter((id) => allergenSet.has(id));
    if (matched.length === 0) {
      return { pal, priority: 'informational' as const, matchedAllergenIds: [] };
    }
    const hasMustAvoid = matched.some((id) => severityMap.get(id) === 'must_avoid');
    const priority: PALPriority = hasMustAvoid ? 'critical' : 'watch';
    return { pal, priority, matchedAllergenIds: matched };
  });
};

// ── F-51 (E5): Severe-Allergen ER Proximity Alert ──────

export const selectSevereAllergenDetected = (state: AppStore): boolean => {
  const profile = state.profiles.find((p) => p.id === state.activeProfileId);
  if (!profile) return false;
  const mustAvoidIds = new Set(
    profile.allergenSeverities
      .filter((s) => s.severity === 'must_avoid')
      .map((s) => s.allergenId)
  );
  if (mustAvoidIds.size === 0) return false;
  const profileAllergens = new Set<string>([
    ...profile.selectedAllergens,
    ...profile.customAllergens.map((ca) => ca.id),
  ]);
  return state.results.some(
    (d) =>
      (d.confidence === Confidence.Present || d.confidence === Confidence.Possible) &&
      profileAllergens.has(d.allergenId) &&
      mustAvoidIds.has(d.allergenId)
  );
};
